// scheduling/components/reports/AdHocReport.jsx
// Ad-hoc pivot over scheduled hours and WIP (Trello #309).
//
// DJB asked for "a SQL query to add to Power BI", then said what he actually wanted:
// "It was more for just ad hoc things that come up. Like today, I wanted to see how many
// hours we had scheduled on EBP Team but that's not currently possible (that I could
// find) in the regular interface."
//
// So this is a group-by + filter tool, not a general 2-D pivot: pick one dimension,
// optionally break it down by week or month, filter, read the hours. That shape answers
// the questions that actually come up ("EBP Team" is rprs.CustomJobs id 1 — 3,570h
// across 22 staff) without the paging and layout problems a full cross-tab brings.
//
// ─── NOTHING RUNS UNTIL YOU PRESS RUN ───────────────────────────────────────
// No query on mount, and none on filter change. A WIP pivot scans 6.8M rows (pe.TranWIP
// has no index on WIPDate or StaffIndex), so an auto-run would spend ~1-2s of shared
// Function worker time every time someone merely opened the tab or ticked a box — for a
// result they had not asked for yet.
//
// ─── TWO PANELS, NOT ONE TABLE ──────────────────────────────────────────────
// Scheduled hours and WIP don't share a universe: custom jobs exist only on the
// scheduled side, PE's ADMIN/PTO service codes only on the WIP side, and closed jobs only
// on the WIP side. Showing them in one table would invite subtracting two numbers that
// aren't comparable, so they sit side by side with their own totals, and a dimension or
// filter one side can't honour says so explicitly rather than returning something that
// merely looks right.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUrlState } from '../../../hooks/useUrlState';
import { fetchAdhocPivot, fetchAdhocDimensions } from '../../services/reportsApi';
import { fetchCustomJobs } from '../../services/schedData';
import { DEPT_GROUPS } from '../../utils/schedConstants';
import { exportCsv } from '../../utils/csv';
import { toISO } from '../../utils/capacity';

const LEVELS = ['Member', 'Principal', 'Senior Manager', 'Manager', 'Supervisor',
  'Senior Accountant', 'Staff A', 'Staff B', 'Intern', 'Consultant'];

// Sensible starting range: the current calendar year. Deliberately a real default rather
// than "all time" — the server refuses an unbounded range, and CY is the window most
// ad-hoc questions are about.
function defaultRange() {
  const y = new Date().getFullYear();
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

const PRESETS = [
  { id: 'cy', label: 'This year', range: () => defaultRange() },
  { id: 'ytd', label: 'YTD', range: () => ({ from: `${new Date().getFullYear()}-01-01`, to: toISO(new Date()) }) },
  { id: 'fwd', label: 'Today onward', range: () => ({ from: toISO(new Date()), to: `${new Date().getFullYear() + 1}-12-31` }) },
  { id: 'q', label: 'Next 90 days', range: () => {
    const a = new Date(); const b = new Date(); b.setDate(b.getDate() + 90);
    return { from: toISO(a), to: toISO(b) };
  } },
];

function Panel({ title, subtitle, panel, groupByLabel, bucket, onExport }) {
  if (!panel) return null;
  if (!panel.available) {
    return (
      <div className="sched-rpt-panel">
        <div className="sched-rpt-panel-head"><span>{title}</span></div>
        <div className="sched-rpt-muted-sm sched-rpt-panel-na">Not applicable — {panel.reason}</div>
      </div>
    );
  }
  return (
    <div className="sched-rpt-panel">
      <div className="sched-rpt-panel-head">
        <span>{title}</span>
        <span className="sched-rpt-panel-total" title="Sum of the rows shown below">
          {panel.totalHours.toLocaleString()}h
        </span>
        <button className="sched-rpt-csv" onClick={onExport}
          title="Download these rows as CSV (UTF-8, Excel-safe quoting)">⭳ CSV</button>
      </div>
      {subtitle && <div className="sched-rpt-panel-sub">{subtitle}</div>}
      {panel.truncated && (
        <div className="sched-rpt-warn">
          ⚠ Showing the top {panel.cap} groups by hours — there are more. Narrow the filters
          or pick a coarser dimension for a complete picture.
        </div>
      )}
      <table className="sched-mgr-table sched-rpt-table">
        <thead>
          <tr className="sched-rpt-colrow">
            <th title={`The dimension you grouped by (${groupByLabel}).`}>{groupByLabel}</th>
            {bucket !== 'none' && <th title="Time bucket — the week-commencing Monday, or the month.">{bucket === 'week' ? 'Week' : 'Month'}</th>}
            <th className="sched-mgr-th-right" title="Sum of hours in this group.">Hours</th>
            <th className="sched-mgr-th-right" title="Number of underlying rows — schedule entries, or WIP transactions.">Rows</th>
            <th className="sched-mgr-th-right" title="Distinct people contributing hours to this group.">People</th>
            <th className="sched-mgr-th-right" title="This group's share of the panel total.">Share</th>
          </tr>
        </thead>
        <tbody>
          {panel.rows.map((r, i) => (
            <tr key={`${r.dim}|${r.bucket}|${i}`}>
              <td>{r.dim}</td>
              {bucket !== 'none' && <td className="sched-rpt-muted-sm">{r.bucket}</td>}
              <td className="sched-mgr-td-right sched-rpt-pct">{r.hours.toLocaleString()}</td>
              <td className="sched-mgr-td-right sched-rpt-muted-sm">{r.entries.toLocaleString()}</td>
              <td className="sched-mgr-td-right sched-rpt-muted-sm">{r.staff}</td>
              <td className="sched-mgr-td-right sched-rpt-muted-sm">
                {panel.totalHours > 0 ? `${Math.round((100 * r.hours) / panel.totalHours)}%` : '—'}
              </td>
            </tr>
          ))}
          {panel.rows.length === 0 && (
            <tr><td colSpan={6} className="sched-rpt-muted">Nothing matched.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function AdHocReport() {
  const [measure, setMeasure] = useUrlState('m', 'both', { validate: ['scheduled', 'wip', 'both'] });
  const [groupBy, setGroupBy] = useUrlState('g', 'staff');
  const [bucket, setBucket] = useUrlState('b', 'none', { validate: ['none', 'week', 'month'] });

  const dflt = useMemo(defaultRange, []);
  const [from, setFrom] = useUrlState('from', dflt.from);
  const [to, setTo] = useUrlState('to', dflt.to);

  // Filters are local state, applied on Run. See the header note on why nothing
  // auto-queries.
  const [depts, setDepts] = useState([]);
  const [levels, setLevels] = useState([]);
  const [customJobIds, setCustomJobIds] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);

  const [meta, setMeta] = useState(null);
  const [customJobs, setCustomJobs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Metadata only — cheap, and needed to render the pickers at all. The pivot itself
    // waits for an explicit Run.
    fetchAdhocDimensions().then(setMeta).catch(e => setError(e.message));
    fetchCustomJobs().then(d => setCustomJobs(d || [])).catch(() => {});
    // No staff picker: dept + level cover the staff-side filtering people have asked
    // for, and a ~300-row staff list would need the search-first treatment the client
    // picker got rather than a plain checklist.
  }, []);

  const run = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchAdhocPivot({
      measure, groupBy, bucket, from, to,
      filters: {
        depts, levels, customJobIds,
        clientSearch: clientSearch.trim() || undefined,
        jobSearch: jobSearch.trim() || undefined,
        activeOnly,
      },
    })
      .then(r => { setData(r); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [measure, groupBy, bucket, from, to, depts, levels, customJobIds, clientSearch, jobSearch, activeOnly]);

  // Which preset, if any, produced the current dates? DERIVED rather than tracked in
  // state: that keeps it correct for a deeplinked ?from/?to, and it clears itself the
  // moment either date is edited by hand — the previous version highlighted nothing on
  // click and then kept a stale highlight after a manual edit.
  const activePreset = useMemo(() => {
    const hit = PRESETS.find(p => { const r = p.range(); return r.from === from && r.to === to; });
    return hit ? hit.id : null;
  }, [from, to]);

  const dims = meta?.dimensions || [];
  const activeDim = dims.find(d => d.key === groupBy);

  const exportPanel = (panel, kind) => {
    const cols = [
      { label: activeDim?.label || groupBy, get: r => r.dim },
      ...(bucket !== 'none' ? [{ label: bucket === 'week' ? 'Week' : 'Month', get: r => r.bucket }] : []),
      { label: 'Hours', get: r => r.hours },
      { label: 'Rows', get: r => r.entries },
      { label: 'People', get: r => r.staff },
    ];
    exportCsv(`rprs-${kind}-by-${groupBy}`, panel.rows, cols);
  };

  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  return (
    <div className="sched-rpt-root">
      {/* ── Query builder ── */}
      <div className="sched-rpt-builder">
        <div className="sched-rpt-brow">
          <label className="sched-rpt-blabel" title="Which hours to total. Scheduled comes from the RPRS calendar; WIP is time actually entered in PE.">Measure</label>
          {[{ id: 'both', l: 'Both' }, { id: 'scheduled', l: 'Scheduled' }, { id: 'wip', l: 'WIP' }].map(m => (
            <button key={m.id} className={`sched-mgr-pill${measure === m.id ? ' sched-mgr-pill--active' : ''}`}
              onClick={() => setMeasure(m.id)}>{m.l}</button>
          ))}

          <label className="sched-rpt-blabel" title="What to group the hours by. Dimensions marked (sched only) exist only for scheduled hours — PE has no equivalent.">Group by</label>
          <select className="sched-rpt-bsel" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            {dims.map(d => (
              <option key={d.key} value={d.key}>
                {d.label}{!d.charged ? ' (sched only)' : ''}
              </option>
            ))}
          </select>

          <label className="sched-rpt-blabel" title="Optionally break each group down over time.">Break down by</label>
          <select className="sched-rpt-bsel" value={bucket} onChange={e => setBucket(e.target.value)}>
            {(meta?.buckets || []).map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </div>

        <div className="sched-rpt-brow">
          <label className="sched-rpt-blabel" title="Required. The WIP table has no date index, so an unbounded range would scan 6.8M rows.">Dates</label>
          <input type="date" className="sched-rpt-bdate" value={from} onChange={e => setFrom(e.target.value)} />
          <span className="sched-rpt-muted-sm">to</span>
          <input type="date" className="sched-rpt-bdate" value={to} onChange={e => setTo(e.target.value)} />
          {PRESETS.map(p => (
            <button key={p.id}
              className={`sched-mgr-pill${activePreset === p.id ? ' sched-mgr-pill--applied' : ''}`}
              title={activePreset === p.id ? 'Currently applied' : 'Set the dates to this range'}
              onClick={() => { const r = p.range(); setFrom(r.from); setTo(r.to); }}>{p.label}</button>
          ))}
        </div>

        <div className="sched-rpt-brow">
          <label className="sched-rpt-blabel" title="Filter by the staff member's department.">Dept</label>
          {DEPT_GROUPS.map(d => (
            <button key={d.id} className={`sched-mgr-pill${depts.includes(d.id) ? ' sched-mgr-pill--active' : ''}`}
              onClick={() => toggle(depts, setDepts, d.id)}>{d.label.split(' ')[0]}</button>
          ))}
        </div>

        <div className="sched-rpt-brow sched-rpt-brow--filters">
          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel" title="Filter by the staff member's level.">Level</label>
            <select className="sched-rpt-bsel" value="" onChange={e => { if (e.target.value) toggle(levels, setLevels, e.target.value); }}>
              <option value="">{levels.length ? `+ add (${levels.length})` : 'all'}</option>
              {LEVELS.filter(l => !levels.includes(l)).map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            {levels.map(l => (
              <button key={l} className="sched-mgr-pill sched-mgr-pill--active"
                title="Click to remove" onClick={() => toggle(levels, setLevels, l)}>{l} ×</button>
            ))}
          </span>

          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel" title="Firm-internal categories (EBP Team, CPE, Training…). Scheduled hours only — PE has no custom jobs, so selecting any of these makes the WIP panel N/A.">Custom job</label>
            <select className="sched-rpt-bsel" value=""
              onChange={e => { if (e.target.value) toggle(customJobIds, setCustomJobIds, Number(e.target.value)); }}>
              <option value="">{customJobIds.length ? `+ add (${customJobIds.length})` : 'all'}</option>
              {customJobs.filter(c => !customJobIds.includes(c.CustomJobId))
                .map(c => <option key={c.CustomJobId} value={c.CustomJobId}>{c.Name}</option>)}
            </select>
            {customJobIds.map(id => {
              const c = customJobs.find(x => x.CustomJobId === id);
              return (
                <button key={id} className="sched-mgr-pill sched-mgr-pill--active"
                  title="Click to remove" onClick={() => toggle(customJobIds, setCustomJobIds, id)}>
                  {c?.Name || id} ×
                </button>
              );
            })}
          </span>
        </div>

        <div className="sched-rpt-brow sched-rpt-brow--filters">
          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel" title="Substring match on the client name.">Client</label>
            <input className="sched-rpt-btext" placeholder="name contains…" value={clientSearch}
              onChange={e => setClientSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && run()} />
          </span>
          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel" title="Substring match on the job name — e.g. “EBP” or “1120S”.">Job</label>
            <input className="sched-rpt-btext" placeholder="name contains…" value={jobSearch}
              onChange={e => setJobSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && run()} />
          </span>
          <label className="sched-rpt-check" title="Exclude departed staff. Turn off to include historical hours from people who have left.">
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
            Active staff only
          </label>
          <button className="sched-rpt-run" onClick={run} disabled={loading || !meta}>
            {loading ? 'Running…' : 'Run'}
          </button>
        </div>
      </div>

      {error && <div className="sched-rpt-error">{error}</div>}

      {/* Deliberate empty state rather than results-on-load. */}
      {!data && !error && !loading && (
        <div className="sched-rpt-muted">
          Choose a measure, a dimension and a date range, then press <strong>Run</strong>.
          Nothing is queried until you do — a WIP pivot scans several million rows.
        </div>
      )}

      {data && !error && (
        <div className="sched-rpt-panels">
          <Panel
            title="Scheduled hours"
            subtitle="From the RPRS calendar — includes custom jobs, excludes jobs closed in PE."
            panel={data.scheduled}
            groupByLabel={data.groupByLabel}
            bucket={data.bucket}
            onExport={() => exportPanel(data.scheduled, 'scheduled')}
          />
          <Panel
            title="WIP (charged in PE)"
            subtitle="Time actually entered in PE — includes closed jobs and PE's own ADMIN/PTO service codes."
            panel={data.charged}
            groupByLabel={data.groupByLabel}
            bucket={data.bucket}
            onExport={() => exportPanel(data.charged, 'wip')}
          />
        </div>
      )}

      <div className="sched-rpt-footnote">
        The two panels are <strong>not</strong> directly subtractable. Scheduled hours come from
        the RPRS calendar and include custom jobs but not jobs already closed in PE; WIP comes
        from PE and includes closed jobs plus PE&rsquo;s own ADMIN and PTO service codes. Each
        panel totals only what it can actually see, and any dimension or filter one side cannot
        honour is marked <em>Not applicable</em> rather than silently returning a different question&rsquo;s answer.
      </div>
    </div>
  );
}
